import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, Image, Tag, DollarSign, Link as LinkIcon, Package, Upload, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import VideoUploader from '@/components/VideoUploader';
import { uploadToCloudinary, uploadErrorMessage, IMAGE_TYPES, MAX_IMAGE_MB } from '@/lib/cloudinaryUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Electronics', 'Fashion', 'Home & Garden', 'Sports', 'Beauty & Health', 'General'
];

export default function MerchantProductFormPage() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const isEditing = !!productId;
  const fileInputRef = useRef(null);
  const additionalFilesRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'General',
    image_url: '',
    images: [],
    videos: [],
    product_type: 'physical',
    affiliate_url: '',
    stock: '0',
    tags: ''
  });
  const [newVideoUrl, setNewVideoUrl] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('merchant_token');
    if (!token) {
      navigate('/merchant/login');
      return;
    }
    
    if (isEditing) {
      loadProduct();
    }
  }, [productId, navigate, isEditing]);

  const loadProduct = async () => {
    setLoading(true);
    const token = localStorage.getItem('merchant_token');
    try {
      const res = await fetch(`${API_URL}/api/merchant/products/${productId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.product) {
        const p = data.product;
        setFormData({
          name: p.name || '',
          description: p.description || '',
          price: p.price?.toString() || '',
          category: p.category || 'General',
          image_url: p.image_url || '',
          images: p.images || [],
          videos: p.videos || [],
          product_type: p.product_type || 'physical',
          affiliate_url: p.affiliate_url || '',
          stock: p.stock?.toString() || '0',
          tags: (p.tags || []).join(', ')
        });
      }
    } catch (error) {
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Upload images straight to Cloudinary (permanent storage)
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, GIF, WebP or HEIC image.');
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      toast.error(`Image is too large. Maximum size is ${MAX_IMAGE_MB}MB.`);
      return;
    }
    setUploading(true);
    try {
      updateField('image_url', await uploadToCloudinary(file, { folder: 'merchants' }));
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error(uploadErrorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  const handleAdditionalImagesUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    if (formData.images.length >= 5) {
      toast.error('Maximum 5 additional images allowed.');
      return;
    }
    setUploadingAdditional(true);
    const newImages = [...formData.images];
    let failed = 0;
    for (const file of files) {
      if (newImages.length >= 5) break;
      if (!IMAGE_TYPES.includes(file.type) || file.size > MAX_IMAGE_MB * 1024 * 1024) { failed++; continue; }
      try {
        newImages.push(await uploadToCloudinary(file, { folder: 'merchants' }));
      } catch {
        failed++;
      }
    }
    updateField('images', newImages);
    if (failed) toast.error(`${failed} image(s) could not be uploaded.`);
    else toast.success('Images uploaded!');
    setUploadingAdditional(false);
  };

  // Remove additional image
  const removeAdditionalImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    updateField('images', newImages);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price || !formData.image_url) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.product_type === 'affiliate' && !formData.affiliate_url) {
      toast.error('Affiliate URL is required for affiliate products');
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('merchant_token');

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        category: formData.category,
        image_url: formData.image_url,
        images: formData.images || [],
        videos: formData.videos || [],
        product_type: formData.product_type,
        affiliate_url: formData.affiliate_url || null,
        stock: parseInt(formData.stock) || 0,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
      };

      const url = isEditing 
        ? `${API_URL}/api/merchant/products/${productId}`
        : `${API_URL}/api/merchant/products`;
      
      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to save product');
      }

      toast.success(isEditing ? 'Product updated!' : 'Product created!');
      navigate('/merchant/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/merchant/dashboard"
            className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {isEditing ? 'Edit Product' : 'Add New Product'}
            </h1>
            <p className="text-slate-400 text-sm">
              {isEditing ? 'Update your product details' : 'Add a new product to your store'}
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Product Type */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="font-medium text-white mb-4">Product Type</h3>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => updateField('product_type', 'physical')}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  formData.product_type === 'physical'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-slate-600 hover:border-slate-500'
                }`}
              >
                <Package className="w-6 h-6 text-blue-400 mb-2" />
                <p className="font-medium text-white">Physical Product</p>
                <p className="text-xs text-slate-400 mt-1">Ship to customers</p>
              </button>
              <button
                type="button"
                onClick={() => updateField('product_type', 'affiliate')}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  formData.product_type === 'affiliate'
                    ? 'border-purple-500 bg-purple-500/10'
                    : 'border-slate-600 hover:border-slate-500'
                }`}
              >
                <LinkIcon className="w-6 h-6 text-purple-400 mb-2" />
                <p className="font-medium text-white">Affiliate Product</p>
                <p className="text-xs text-slate-400 mt-1">Link to external store</p>
              </button>
            </div>
          </div>

          {/* Basic Info */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
            <h3 className="font-medium text-white mb-2">Basic Information</h3>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Product Name *</label>
              <input
                type="text"
                data-testid="product-name"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                placeholder="Enter product name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none resize-none"
                placeholder="Describe your product..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Price ($) *</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="number"
                    step="0.01"
                    data-testid="product-price"
                    value={formData.price}
                    onChange={(e) => updateField('price', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => updateField('category', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {formData.product_type === 'physical' && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Stock Quantity</label>
                <input
                  type="number"
                  value={formData.stock}
                  onChange={(e) => updateField('stock', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  placeholder="0"
                />
              </div>
            )}
          </div>

          {/* Image */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="font-medium text-white mb-4">Product Image</h3>
            
            {/* Main Image Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-300 mb-2">Main Image *</label>
              
              {/* Upload Button */}
              <div className="flex gap-3 mb-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="merchant-image-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload Image
                    </>
                  )}
                </button>
                <span className="text-slate-400 text-sm self-center">or paste URL below</span>
              </div>
              
              {/* URL Input */}
              <div className="relative">
                <Image className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="url"
                  data-testid="product-image"
                  value={formData.image_url}
                  onChange={(e) => updateField('image_url', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>
            
            {/* Main Image Preview */}
            {formData.image_url && (
              <div className="mb-4">
                <img
                  src={formData.image_url}
                  alt="Preview"
                  className="w-32 h-32 object-cover rounded-lg"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
            
            {/* Additional Images */}
            <div className="border-t border-slate-700 pt-4 mt-4">
              <label className="block text-sm font-medium text-slate-300 mb-3">
                Additional Images (up to 5) - Optional
              </label>
              
              <div className="flex flex-wrap gap-3">
                {/* Existing additional images */}
                {formData.images.map((img, index) => (
                  <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden group">
                    <img src={img} alt={`Product ${index + 2}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeAdditionalImage(index)}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                
                {/* Add more button */}
                {formData.images.length < 5 && (
                  <>
                    <input
                      ref={additionalFilesRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAdditionalImagesUpload}
                      data-testid="merchant-additional-images-input"
                      multiple
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => additionalFilesRef.current?.click()}
                      disabled={uploadingAdditional}
                      className="w-20 h-20 border-2 border-dashed border-slate-600 rounded-lg flex flex-col items-center justify-center text-slate-400 hover:border-blue-500 hover:text-blue-400 transition-colors"
                    >
                      {uploadingAdditional ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-5 h-5" />
                          <span className="text-xs mt-1">Add</span>
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Product Videos */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="font-medium text-white mb-4">Product Videos</h3>
            <p className="text-sm text-slate-400 mb-4">Upload a video file (up to 100MB) or paste a YouTube / MP4 link. Max 3 videos.</p>
            {(!formData.videos || formData.videos.length < 3) && (
              <VideoUploader
                className="w-full mb-3 border-slate-600 text-slate-200 hover:bg-slate-700"
                onUploaded={(url) => setFormData((prev) => ({ ...prev, videos: [...(prev.videos || []), url] }))}
              />
            )}
            
            {/* Existing videos */}
            {formData.videos && formData.videos.length > 0 && (
              <div className="space-y-2 mb-4">
                {formData.videos.map((video, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 bg-slate-900/50 rounded-lg">
                    <span className="text-sm text-slate-300 truncate flex-1">{video}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const newVideos = formData.videos.filter((_, i) => i !== index);
                        updateField('videos', newVideos);
                      }}
                      className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add new video */}
            {(!formData.videos || formData.videos.length < 3) && (
              <div className="flex gap-2">
                <input
                  type="url"
                  value={newVideoUrl}
                  onChange={(e) => setNewVideoUrl(e.target.value)}
                  className="flex-1 px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  placeholder="https://youtube.com/watch?v=..."
                />
                <button
                  type="button"
                  onClick={() => {
                    if (newVideoUrl.trim()) {
                      const newVideos = [...(formData.videos || []), newVideoUrl.trim()];
                      updateField('videos', newVideos);
                      setNewVideoUrl('');
                    }
                  }}
                  disabled={!newVideoUrl.trim()}
                  className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            )}
          </div>

          {/* Affiliate URL */}
          {formData.product_type === 'affiliate' && (
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="font-medium text-white mb-4">Affiliate Link</h3>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Affiliate URL *</label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="url"
                    data-testid="affiliate-url"
                    value={formData.affiliate_url}
                    onChange={(e) => updateField('affiliate_url', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    placeholder="https://affiliate-link.com/product"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Enter your affiliate link. Customers will be redirected here when they click &quot;Buy Now&quot;.
                </p>
              </div>
            </div>
          )}

          {/* Tags */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="font-medium text-white mb-4">Tags</h3>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Product Tags</label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => updateField('tags', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  placeholder="tech, gadget, wireless (comma separated)"
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Link
              to="/merchant/dashboard"
              className="flex-1 py-3 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-800 transition-colors text-center"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              data-testid="save-product"
              className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-500 hover:to-purple-500 transition-all font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {saving ? 'Saving...' : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? 'Update Product' : 'Create Product'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
